/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useSpring, animated } from '@react-spring/web';
import { IconButton, type IconComponentType } from '../icon-button';
import { cn } from '@restworld/utils-common';

interface AccordionProps {
  children: React.ReactNode;
  open?: boolean;
  className?: string;
}

interface AccordionContextType {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AccordionContext = React.createContext<AccordionContextType>({
  isOpen: false,
  setIsOpen: () => {},
});

export const useAccordionContext = () => React.useContext(AccordionContext);

interface AccordionButtonProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  /** @deprecated Use `className` on ButtonContentWrapper instead */
  wrapperStyle?: string;
  IconComponent?: IconComponentType;
}

interface AccordionPanelProps {
  children?: React.ReactNode;
  className?: string;
}

/**
 * Accordion component with animated expand/collapse behavior.
 *
 * Compound component usage:
 * ```
 * <Accordion open={false}>
 *   <Accordion.Button>Toggle</Accordion.Button>
 *   <Accordion.Panel>Content</Accordion.Panel>
 * </Accordion>
 * ```
 */
export const Accordion = ({ children, open, className, ...rest }: AccordionProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  React.useEffect(() => {
    if (typeof open !== 'undefined') setIsOpen(open);
  }, [open]);
  return (
    <AccordionContext.Provider value={{ isOpen, setIsOpen }}>
      <div className={className} {...rest}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

/** Backward-compatible alias */
export const AccordionV2 = Accordion;

export default Accordion;

const AccordionButton = ({
  children,
  onClick,
  icon,
  wrapperStyle,
  IconComponent,
  className,
  ...rest
}: AccordionButtonProps) => {
  const { isOpen, setIsOpen } = useAccordionContext();

  if (IconComponent) {
    return (
      <ButtonContentWrapper className={cn(wrapperStyle, className)}>
        {children}
        <StyledIconContainer isOpen={isOpen}>
          <IconButton
            ariaLabel=""
            Icon={IconComponent}
            onClick={(e) => {
              onClick?.(e as unknown as React.MouseEvent<HTMLElement>);
              setIsOpen(!isOpen);
            }}
          />
        </StyledIconContainer>
      </ButtonContentWrapper>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={(e) => {
        onClick?.(e as unknown as React.MouseEvent<HTMLElement>);
        setIsOpen(!isOpen);
      }}
      {...rest}
    >
      <ButtonContentWrapper className={cn(wrapperStyle)}>
        {children}
        <StyledIconContainer isOpen={isOpen}>
          {icon ? icon : <ChevronDownIcon className="h-4 w-4" />}
        </StyledIconContainer>
      </ButtonContentWrapper>
    </button>
  );
};

const AccordionPanel = ({ children, className, ...rest }: AccordionPanelProps) => {
  const { isOpen } = useAccordionContext();
  const accordionAnimation = useSpring({
    height: isOpen ? 'auto' : 0,
    opacity: isOpen ? 1 : 0,
    marginTop: isOpen ? 0 : -3.5,
    config: { tension: 300, friction: 20 },
  });

  return (
    <animated.div style={{ ...accordionAnimation, overflow: 'hidden' }}>
      <div className={className} {...rest}>
        {children}
      </div>
    </animated.div>
  );
};

const StyledIconContainer = ({
  isOpen,
  className,
  ...props
}: { isOpen: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('h-fit w-fit transform transition-transform duration-150', isOpen && 'rotate-180', className)}
    {...props}
  />
);

const ButtonContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex justify-between items-center', className)} {...props} />
);

Accordion.Button = AccordionButton;
Accordion.Panel = AccordionPanel;

// Also attach to the alias so AccordionV2.Button / AccordionV2.Panel work
AccordionV2.Button = AccordionButton;
AccordionV2.Panel = AccordionPanel;
